import { setGA4Event } from "@iewcasia/Store/Handlers/Utilities/CustomTracking";
import { sanitizePostAuthReturnUrl } from "@iewcasia/Utilities/sanitizePostAuthReturnUrl";
import { addToChainAfter, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getCart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import { getBillTo } from "@insite/client-framework/Services/CustomersService";
import { Session } from "@insite/client-framework/Services/SessionService";
import {
    chain,
    DispatchCompleteSignIn,
    NavigateToReturnUrl,
} from "@insite/client-framework/Store/Context/Handlers/SignIn";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

/** Removes the OOTB forcereturnurl query flag. */
const stripForceReturnUrlFlag = (url?: string): string | undefined => {
    if (!url) {
        return url;
    }

    return url
        .replace(/([?&])forcereturnurl(?=&|#|$)/gi, "$1")
        .replace(/\?&/, "?")
        .replace(/[?&]$/, "");
};

/** Path only, lowercased, without trailing slash. */
const normalizePath = (url?: string): string =>
    (url?.split("?")[0] || "/").toLowerCase().replace(/\/$/, "") || "";

/**
 * SSO appends forcereturnurl even for "/". Only force when the destination is a real page
 * (checkout, etc.), so Dashboard Is Homepage still wins for bare home.
 */
const shouldForceReturnUrl = (returnUrl: string | undefined, homePageUrl: string): boolean => {
    if (!returnUrl || !/(?:\?|&)forcereturnurl(?:&|#|$)/i.test(returnUrl)) {
        return false;
    }

    const stripped = stripForceReturnUrlFlag(returnUrl);
    const returnPath = normalizePath(stripped);
    const homePath = normalizePath(homePageUrl);

    return !!(stripped && returnPath !== "" && returnPath !== "/" && returnPath !== homePath);
};

/** Resolves dashboard or custom landing page when Dashboard Is Homepage / custom landing apply. */
const resolveDefaultLandingUrl = (session: Session, dashboardPageUrl?: string): string => {
    if (session.isGuest) {
        return "";
    }

    if (session.dashboardIsHomepage && dashboardPageUrl) {
        return dashboardPageUrl;
    }

    return session.customLandingPage || "";
};

/** True when returnUrl is an external order-approval details link that must override default landing. */
const shouldBypassDefaultForOrderApproval = (returnUrl: string | undefined, orderApprovalDetailsPageUrl?: string) =>
    !!(
        returnUrl &&
        orderApprovalDetailsPageUrl &&
        returnUrl.toLowerCase().indexOf(orderApprovalDetailsPageUrl.toLowerCase()) > -1 &&
        (returnUrl.toLowerCase().indexOf("external-approval=") > -1 ||
            returnUrl.toLowerCase().indexOf("external-approval-view=") > -1)
    );

type CheckoutReturnContext = {
    returnUrl?: string;
    isRFQGuest: boolean;
    isCheckoutGuest: boolean;
    isGuest: boolean;
    isRestrictedProductExistInCart: boolean;
    checkoutShippingUrl?: string;
    rfqGuestPageUrl?: string;
    checkoutReviewAndSubmitUrl?: string;
    cartUrl?: string;
};

/** Applies cart/checkout/RFQ adjustments when returning to shipping (or RFQ guest). */
const resolveCheckoutOrRfqReturnUrl = async (ctx: CheckoutReturnContext): Promise<string | undefined> => {
    const returnPath = normalizePath(stripForceReturnUrlFlag(ctx.returnUrl));
    if (returnPath !== ctx.checkoutShippingUrl?.toLowerCase() && !ctx.isRFQGuest) {
        return ctx.returnUrl;
    }

    const cartResult = await getCart({
        cartId: API_URL_CURRENT_FRAGMENT,
        expand: ["cartLines", "hiddenproducts"],
    } as GetCartApiParameter);

    const { canCheckOut, canBypassCheckoutAddress } = cartResult.cart;

    if ((!canCheckOut || ctx.isRestrictedProductExistInCart) && !ctx.isRFQGuest && !ctx.isCheckoutGuest) {
        return ctx.cartUrl;
    }

    if (ctx.isRFQGuest) {
        return `${ctx.rfqGuestPageUrl}?cartId=${cartResult.cart.id}`;
    }

    if (ctx.isGuest && ctx.isCheckoutGuest) {
        return ctx.checkoutShippingUrl;
    }

    if (canBypassCheckoutAddress) {
        return ctx.checkoutReviewAndSubmitUrl;
    }

    return ctx.returnUrl;
};

/** Home returnUrl â†’ custom landing or dashboard when configured. */
const applyHomeLandingOverrides = (
    returnUrl: string | undefined,
    homePageUrl: string,
    session: Session,
    defaultUrl: string,
    dashboardPageUrl?: string,
): string | undefined => {
    if (returnUrl?.toLowerCase() !== homePageUrl.toLowerCase()) {
        return returnUrl;
    }

    if (session.customLandingPage) {
        let base = returnUrl.endsWith("/") ? returnUrl.substring(0, returnUrl.length - 1) : returnUrl;
        if (session.customLandingPage.startsWith("/")) {
            return `${base}${session.customLandingPage}`;
        }

        return `${base}/${session.customLandingPage}`;
    }

    if (session.dashboardIsHomepage && dashboardPageUrl) {
        return defaultUrl;
    }

    return returnUrl;
};

addToChainAfter(chain, DispatchCompleteSignIn, async props => {
    if (props.authenticatedSession) {
        const billTo = props.authenticatedSession?.isAuthenticated
            ? await getBillTo({
                  billToId: API_URL_CURRENT_FRAGMENT,
              })
            : null;

        const sapAccountNumber =
            props.authenticatedSession.isAuthenticated && billTo
                ? billTo.customerNumber.replace(/^0+/, "")
                : "__IS_LOADING__";

        const userID = props.authenticatedSession.userProfileId;

        setGA4Event([], "sign_in", { userID, sapAccountNumber });
    }
});

replaceInChain(chain, NavigateToReturnUrl, async props => {
    if (!props.authenticatedSession) {
        return;
    }

    const state = props.getState() as ApplicationState;
    const session = props.authenticatedSession;
    let returnUrl = sanitizePostAuthReturnUrl(props.parameter.returnUrl);
    const dashboardPageUrl = getPageLinkByPageType(state, "MyAccountPage")?.url;
    const homePageUrl = getHomePageUrl(state);
    const defaultUrl = resolveDefaultLandingUrl(session, dashboardPageUrl);
    const orderApprovalDetailsPageUrl = getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url;
    const rfqGuestPageUrl = getPageLinkByPageType(state, "RfqRequestQuoteGuestAddressPage")?.url;
    const isRFQGuest = returnUrl?.toLowerCase() === rfqGuestPageUrl?.toLowerCase();
    const forceReturn = shouldForceReturnUrl(returnUrl, homePageUrl);
    const bypassDefaultUrlIfOrderDetails = shouldBypassDefaultForOrderApproval(
        props.parameter.returnUrl,
        orderApprovalDetailsPageUrl,
    );

    if (defaultUrl !== "" && !bypassDefaultUrlIfOrderDetails && !isRFQGuest && !forceReturn) {
        window.location.href = defaultUrl;
        return;
    }

    returnUrl = await resolveCheckoutOrRfqReturnUrl({
        returnUrl,
        isRFQGuest,
        isCheckoutGuest: returnUrl?.toLowerCase() === rfqGuestPageUrl?.toLowerCase(),
        isGuest: session.isGuest,
        isRestrictedProductExistInCart: session.isRestrictedProductExistInCart,
        checkoutShippingUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        rfqGuestPageUrl,
        checkoutReviewAndSubmitUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    });

    if (returnUrl?.includes("SwitchingLanguage")) {
        returnUrl = returnUrl.split("?")[0];
    }

    returnUrl = stripForceReturnUrlFlag(returnUrl);
    returnUrl = applyHomeLandingOverrides(returnUrl, homePageUrl, session, defaultUrl, dashboardPageUrl);

    window.location.href = returnUrl || homePageUrl;
});
